<template>
	<table class="table table-bordered">
		<thead>
			<tr>
				<th>
					{{ $t('donation-form.summary_details_col_header') }}
				</th>
				<th class="text-center" style="width:150px;">
					{{ $t('donation-form.summary_quantity_col_header') }}
				</th>
				<th class="text-right" style="width:100px;">
					{{ $t('donation-form.summary_amount_col_header') }}
				</th>
			</tr>
		</thead>
		<tbody>
			<template v-for="(campaign, key) in summary" :key="campaign">
				<template v-if="key != '_fbp'">
					<tr class="bg-light text-bold-500">
						<td colspan="3">
							{{ key }}

							<!-- Campaign Statistics -->
							<div class="mt-3 bg-white p-3 border" v-if="formData.show_campaign_statistics">
								<div class="d-flex justify-content-between">

									<!-- Amount raised -->
									<div class="text-13 mb-2">
										<span class="text-normal">
											{{ $t('donation-form.amount_raised') }}
										</span>
										<div class="text-32 font-weight-bold text-left text-green">
											{{ vueNumberFormat(campaigns.find((item) => item.name === key).transactions_sum_amount) }}
										</div>
									</div>
								</div>

								<!-- Progress Bar -->
								<div class="progress-bar-wrapper">
									<div class="progress" style="height: 24px;">
										<div class="progress-bar-striped progress-bar-animated bg-success text-white text-center" role="progressbar" :aria-valuenow="campaigns.find((item) => item.name === key).amount_raised_percentage" aria-valuemin="0" aria-valuemax="100" :style="{ width: campaigns.find((item) => item.name === key).amount_raised_percentage + '%' }">
											<div class="progress-percent-value text-white">
												{{ (campaigns.find((item) => item.name === key).amount_raised_percentage) }}%
											</div>
										</div>
									</div>
								</div>

								<!-- Targeted amount -->
								<div class="mt-2">
									<div class="text-13 text-normal text-left">
										{{ $t('donation-form.target') }}
										<b>{{ vueNumberFormat(campaigns.find((item) => item.name === key).targeted_amount) }}</b>
									</div>
								</div>
							</div>
						</td>
					</tr>

					<template v-for="cpackage in campaign" :key="cpackage.id">
						<tr>
							<td class="align-middle">
								{{ cpackage.name }}

								<div>
					                <span class="text-bold-500" v-if=" ! cpackage.custom_amount_enabled">
						                {{ vueNumberFormat(cpackage.amount) }}
					                </span>
									<span class="text-red text-13" v-else>
										(Minimum {{ vueNumberFormat(cpackage.min_custom_amount) }})
					                </span>
								</div>
							</td>

							<!-- Custom amount enabled else -->
							<td v-if="! cpackage.custom_amount_enabled" class="align-middle text-center">
								<div class="input-group mobile-fix-input-group">

									<!-- Plus + -->
									<span class="input-group-btn input-plus" style="margin-right:2px;">
										<button type="button" class="btn btn-success btn-number btn-sm" style="font-size:10px!important;margin-top:2px;" @click.prevent="$emit('plus', cpackage)">
											<span class="fa fa-plus"></span>
										</button>
									</span>

									<input type="number" inputmode="numeric" class="form-control form-control-sm input-number text-center mr-1 ml-1 mobile-quantity-minus-plus" v-model="cpackage.count" @keypress="onlyNumber($event)" min="0" max="100" @blur="validateQuantity($event, cpackage)" @keyup="validateQuantity($event, cpackage)">

									<!-- Minus - -->
									<span class="input-group-btn input-minus">
										<button type="button" class="btn btn-danger btn-number btn-sm" style="font-size:10px!important;margin-top:2px;" @click.prevent="$emit('minus', cpackage)">
											<span class="fa fa-minus"></span>
										</button>
									</span>
								</div>
							</td>

							<!-- Custom amount enabled -->
							<td v-if="cpackage.custom_amount_enabled" colspan="2" class="align-middle text-center">
								<span class="text-bold-500 mr-1">
									{{ cpackage.count }} x
								</span>
								<div class="input-group d-inline-flex" style="width:130px;">
									<div class="input-group-prepend">
										<span class="input-group-text text-bold-500" id="basic-addon1">RM</span>
									</div>
									<input type="number" inputmode="numeric" :min="cpackage.min_custom_amount" v-model="cpackage.amount" step="1" min="0" :class="{ 'form-control': true, 'text-right': true, 'is-invalid': errors[`${cpackage.key}`] || null }">
									<div class="invalid-feedback" v-if="errors[`${cpackage.key}`] || null">
										{{ errors[`${cpackage.key}`] }}
									</div>
								</div>
							</td>

							<!-- Custom amount enabled else -->
							<td v-else class="align-middle text-right">
								{{ vueNumberFormat(cpackage.amount * cpackage.count) }}
							</td>
						</tr>
					</template>
				</template>
			</template>
		</tbody>
		<tfoot>
			<tr>
				<th colspan="2" class="align-middle text-right">
					{{ $t('donation-form.total') }}
				</th>
				<th class="align-middle text-right">
					{{ vueNumberFormat(total) }}
				</th>
			</tr>

			<!-- Payment Gateway Fees -->
			<tr v-if="formData.payment_gateway_fees_enabled">
				<th colspan="3" class="align-middle text-right">
					<span class="text-normal alert-warning p-1">
                        {{ $t('donation-form.payment_gateway_fees').replace('#amount#', props.formData.payment_gateway_fees) }}
					</span>
				</th>
			</tr>
		</tfoot>
	</table>
</template>

<!-- JS -->
<script setup>
	import useHelpers from "@/Composables/UseHelpers";

	const {onlyNumber, $t, makeSlug} = useHelpers();

	const props = defineProps({
		formData: Object,
		donations: Object,
		summary: Object,
		campaigns: Object,
		errors: Object,
		total: {
			type: Number,
			default: 0
		}
	});

	function validateQuantity(evt, cpackage) {
		if (cpackage.count > 100) {
			cpackage.count = 100;
			alert('You can only select maximum quantity of 100 for each package.');
			focus(evt.currentTarget);
		}
	}
</script>

<!-- CSS -->
<style>
	.select2-container .select2-selection--single .select2-selection__rendered {
		margin-top: 1px;
	}

	/* Chrome, Safari, Edge, Opera */
	input::-webkit-outer-spin-button,
	input::-webkit-inner-spin-button {
		-webkit-appearance: none;
		margin: 0;
	}

	/* Firefox */
	input[type=number] {
		-moz-appearance: textfield;
	}
</style>
